import { round } from "lodash";
import defaults from "lodash/defaults";
import find from "lodash/find";
import get from "lodash/get";
import groupBy from "lodash/groupBy";
import uniqBy from "lodash/uniqBy";
import moment from "moment";
import { DATE_FORMATS } from "../../../constants";
import CalendarAddExerciseModal from "../../../parts/modals/calendar-add-exercise";
import CalendarDuplicateWorkoutModal from "../../../parts/modals/calendar-duplicate-workout";
import CalendarMoveWorkoutModal from "../../../parts/modals/calendar-move-workout";
import ImportWorkoutModal from "../../../parts/modals/import-workout";
import JWPlayerVideoModal from "../../../parts/modals/jwplayer-video";
import SwapExerciseModal from "../../../parts/modals/swap-exercise";
import {
  formattedTarget,
  getAllSetsCounts,
  getLiftsSetsVolume,
  getWeightedIntensity,
} from "../../../utils/actuals";
import {
  buildPreviousOutcomesMap,
  TREND_LOOKBACK_WEEKS,
} from "../../../utils/calendar-trends";
import {
  buildVideoModalUrl,
  findVideoNeighbors,
} from "../../../utils/video-modal-url";

const { DATE_DAY_FORMAT, UTC } = DATE_FORMATS;

import ActivityDrawer from "../../../parts/activity-drawer/ActivityDrawer";
import { loadClientConfigurationForAthlete } from "../../../utils/load-athlete-client-configuration";
import CalendarWeek from "./CalendarWeek";
import { isAdminOrOwner } from "../../../utils/organization-permissions-helpers";

function computeDailyWorkoutSummary(exercises) {
  let totalVolume = 0;
  let completedExercises = 0;
  const plannedExercises = exercises.length;
  let totalReps = 0;
  let weightedIntensityNum = 0;
  let weightedIntensityDen = 0;

  for (const exercise of exercises) {
    const { lifts, volume } = getLiftsSetsVolume(exercise);
    // A metcon has no discrete sets, so getAllSetsCounts collapses it to a
    // single total/completed pair; every exercise (metcon or otherwise)
    // counts as one completed unit once all of its sets are done.
    const { total, completed } = getAllSetsCounts(exercise);

    totalVolume += volume;
    totalReps += lifts;
    if (total > 0 && completed >= total) completedExercises += 1;

    if (volume > 0) {
      const intensity = getWeightedIntensity(exercise, volume);
      weightedIntensityNum += intensity * volume;
      weightedIntensityDen += volume;
    }
  }

  const completionPct =
    plannedExercises > 0
      ? Math.round((completedExercises / plannedExercises) * 100)
      : 0;
  const avgIntensityPct =
    weightedIntensityDen > 0
      ? Math.round((weightedIntensityNum / weightedIntensityDen) * 100)
      : 0;

  return {
    totalVolume,
    completedExercises,
    plannedExercises,
    completionPct,
    avgIntensityPct,
    totalReps,
  };
}

const CalendarWeekController = async ({ req, res, locals, load }, next) => {
  if (req.query.view !== "week") return next();

  const { query = {} } = req;
  defaults(query, { week: moment().format(DATE_DAY_FORMAT), relTrends: false });
  const modal = get(query, "modal", {});

  const weekDay = locals.activeUser.startingCalendarDay || 0;
  locals.activeWeek = moment(query.week, DATE_DAY_FORMAT)
    .startOf("week")
    .weekday(weekDay);
  locals.endOfWeek = locals.activeWeek.clone().add(6, "days");

  const calendarRange = Math.min(
    Math.max(parseInt(query.range, 10) || 1, 1),
    24
  );
  if (calendarRange > 1) {
    locals.endOfWeek = locals.activeWeek
      .clone()
      .add(calendarRange * 7 - 1, "days");
  }
  locals.calendarRange = calendarRange;

  const start = query.start
    ? moment(query.start, DATE_DAY_FORMAT)
    : locals.activeWeek;
  const end = query.end ? moment(query.end, DATE_DAY_FORMAT) : locals.endOfWeek;

  const activeUser = locals.activeUser;
  const athlete = query.athlete || get(locals, "athlete._id");

  await Promise.all([
    load("activeExercises", {
      start,
      end,
      proxyAthlete: query.athlete,
      reqSource: "calendar",
    }),
    load("athleteFlexibleProgram", { proxyAthlete: athlete }),
    load("activeSections", {
      start,
      end,
      proxyAthlete: query.athlete,
      reqSource: "calendar",
    }),
    load(
      "workoutNotes",
      query.athlete,
      start.format("YYYY-MM-DD"),
      end.format("YYYY-MM-DD")
    ),
    load(
      "workoutCoachNotes",
      query.athlete,
      query.organizationCoach || activeUser._id,
      start.format("YYYY-MM-DD"),
      end.format("YYYY-MM-DD")
    ),
    load("dailyStats", {
      proxyAthlete: query.athlete,
      start: start.format("YYYY-MM-DD"),
      end: end.format("YYYY-MM-DD"),
    }),
    load("dayPersonalRecords", {
      start: start.clone().startOf("day").format(UTC),
      end: end.isAfter(moment(), "day")
        ? moment().startOf("day").format(UTC)
        : end.clone().endOf("day").format(UTC),
      proxyAthlete: query.athlete,
      isCheckIn: true,
    }),
  ]);

  locals.scheduledSessions = groupBy(locals.activeExercises, (e) =>
    moment.utc(e.utcDate?.toString() || e.date).format(DATE_DAY_FORMAT)
  );

  locals.scheduledSections = groupBy(locals.activeSections, (s) =>
    moment.utc(s.utcDate?.toString() || s.date).format(DATE_DAY_FORMAT)
  );

  const cycleIds = uniqBy(
    locals.activeExercises.filter((e) => e.cycle),
    "cycle.id"
  ).map((e) => e.cycle.id);
  locals.cycles = cycleIds.length ? await load("cycles", cycleIds) : [];

  locals.dailyWorkoutSummaries = {};
  for (const [dateKey, exercises] of Object.entries(locals.scheduledSessions)) {
    locals.dailyWorkoutSummaries[dateKey] =
      computeDailyWorkoutSummary(exercises);
  }

  if (query.relTrends) {
    const trendHistoryStart = locals.activeWeek
      .clone()
      .subtract(TREND_LOOKBACK_WEEKS, "weeks");
    const trendHistoryEnd = locals.activeWeek.clone().subtract(1, "day");
    const trendHistory = await load("exerciseTrendHistory", {
      start: trendHistoryStart,
      end: trendHistoryEnd,
      proxyAthlete: query.athlete,
    });
    locals.previousOutcomes = buildPreviousOutcomesMap(
      locals.activeExercises || [],
      trendHistory || [],
      weekDay
    );
  } else {
    locals.previousOutcomes = null;
  }

  const isAllowToViewAllOrganizationsAthletes = isAdminOrOwner(
    locals.organizationRoleName
  );
  if (
    locals.activeUser.organizationId &&
    isAllowToViewAllOrganizationsAthletes &&
    !locals.allAthletesFromOrganization
  ) {
    await load("allAthletesFromOrganization");
  }

  if (modal.type === "calendar-add-exercise" && query.addType === "complex") {
    await load("complexes");
  }

  if (modal.type === "import-workout") {
    await load("savedWorkouts");
    await load("workoutFolders");
  }

  if (query.threadId) {
    await load("comments", {
      threadId: query.threadId,
    });
  } else {
    locals.comments = [];
  }

  let prevHref;
  let nextHref;
  if (modal.type === "exercise-set-video" && query.utcDate) {
    const dayKey = moment.utc(String(query.utcDate)).format(DATE_DAY_FORMAT);
    const dayExercises = locals.scheduledSessions?.[dayKey] || [];
    const sequence = [];
    for (const exercise of dayExercises) {
      const exerciseId = String(exercise._id);
      const primary = exercise.primary ?? "";
      const secondary = exercise.secondary ?? "";
      const type = exercise.type ?? "";
      const movement = exercise.movement ?? "";
      (exercise.sets || []).forEach((set, setIdx) => {
        const videos = set.videos || {};
        Object.keys(videos)
          .map(Number)
          .sort((a, b) => a - b)
          .forEach((vidIdx) => {
            const video = videos[vidIdx];
            if (!video?.videoOriginal) return;
            const seqOutcomeObj = set.outcomes?.[vidIdx]?.outcome || {};
            const seqIsPowerlifting =
              exercise.type === "primary" || exercise.type === "secondary";
            const { units, intensityMeasure } = locals.activeUser;
            const seqPrecision = units === "metric" ? 2 : 1;
            const seqUserMetrics = units === "imperial" ? "lb" : "kg";
            const seqRawLoad = seqOutcomeObj.load;
            const seqLoad = seqRawLoad
              ? `${round(locals.fromMetric(seqRawLoad, units), seqPrecision)} ${seqUserMetrics}`
              : 0;
            const seqTarget =
              seqRawLoad || seqOutcomeObj.reps || seqOutcomeObj.rpe
                ? formattedTarget(
                    seqOutcomeObj,
                    seqIsPowerlifting,
                    set.percentRepMax || undefined,
                    units,
                    seqLoad,
                    intensityMeasure,
                    intensityMeasure !== "rpe",
                    true
                  ) || undefined
                : undefined;
            const seqSetNumber = setIdx + 1;
            sequence.push({
              key: `${exerciseId}:${setIdx}:${vidIdx}`,
              href: buildVideoModalUrl({
                modalType: "exercise-set-video",
                video,
                exerciseToEdit: exerciseId,
                setIndex: setIdx,
                videoIndex: vidIdx,
                utcDate: exercise.utcDate,
                primary,
                secondary,
                type,
                movement,
                setNumber: seqSetNumber,
                setTarget: seqTarget,
                setComment: set.comments?.[vidIdx] || undefined,
              }),
            });
          });
      });
    }
    const currentKey = `${query.exerciseToEdit}:${Number(query.setIndex)}:${Number(query.videoIndex)}`;
    ({ prevHref, nextHref } = findVideoNeighbors({ sequence, currentKey }));
  }

  if (query.athlete) {
    await loadClientConfigurationForAthlete(load, query.athlete);
  }

  const editExercise = query.editMode
    ? find(locals.activeExercises, { _id: query.exercise })
    : {};

  locals.modal = {
    "exercise-set-video": (
      <JWPlayerVideoModal
        athleteId={query.uploaderId || query.athlete}
        exerciseToEdit={query.exerciseToEdit}
        locals={locals}
        mediaId={query.mediaId}
        movement={query.movement}
        originalId={query.originalId}
        primary={query.primary}
        reviewedByTheCoach={query.reviewedByTheCoach}
        secondary={query.secondary}
        setIndex={query.setIndex}
        threadId={query.threadId}
        type={query.type}
        utcDate={query.utcDate}
        videoIndex={query.videoIndex}
        prevHref={prevHref}
        nextHref={nextHref}
        setNumber={query.setNumber}
        setTarget={query.setTarget}
        setComment={query.setComment}
      />
    ),
    "calendar-add-exercise": (
      <CalendarAddExerciseModal
        editExercise={editExercise}
        reqSource="calendar"
      />
    ),
    "calendar-duplicate-workout": <CalendarDuplicateWorkoutModal />,
    "calendar-move-workout": <CalendarMoveWorkoutModal />,
    "swap-exercise": <SwapExerciseModal exercise={editExercise} />,
    "import-workout": (
      <ImportWorkoutModal
        savedWorkouts={locals.savedWorkouts || []}
        workoutFolders={locals.workoutFolders || []}
        date={query.day}
        exercisesLength={query.exercisesLength}
        highestOrder={query.highestOrder}
        athlete={locals.athlete || locals.activeUserToRequest}
      />
    ),
    "activity-feed": <ActivityDrawer />,
  }[modal.type];

  res.body = <CalendarWeek />;
};

export default CalendarWeekController;
